import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { IWalletTransaction } from '../../interfaces/finance';
import { Box } from '@mui/material';
import { exportToExcel } from '../../utils/file';
import { useDebounce } from 'use-debounce';

export default function ReportListTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<IWalletTransaction[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const properties: (keyof IWalletTransaction)[] = ['title'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);
	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'financial_report', t });
	};

	const columns: GridColDef<IWalletTransaction>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			width: 150,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'wallet',
			headerName: t('wallet'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'amount',
			headerName: t('amount'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				const value = params.row.debtor ? params.row.debtor + '+' : params.row.creditor + '-';
				const color = params.row.debtor ? 'text-green-600' : 'text-red-600';
				return (
					<span className={`${color}`} style={{ direction: 'rtl' }}>
						{value}
					</span>
				);
			},
		},
		{
			field: 'ref_type',
			headerName: t('authority'),
			width: 120,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'balance',
			headerName: t('balance_after_this_transaction'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('date'),
			width: 140,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
	];

	const getServer = () => {
		setLoading(true);
		axios<IResponse<IWalletTransaction[]>>({
			url: '/accounting-profile/wallet-transactions',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<Box className="w-full">
			<BaranDataGrid<IWalletTransaction> pagination={pagination} loading={loading} rows={fetchedData} columns={columns as GridColDef<IWalletTransaction>[]} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} searchableFields={properties} />
		</Box>
	);
}
